import { SUBSCRIPTION_KEYS } from "@components/subscriptions/queries/subscription.queries";
import { PhoneAccessRequest } from "@components/virtual-receptionist/porting-request/PhoneAccessRequest";
import { PortingFormData } from "@components/virtual-receptionist/porting-request/hooks/interfaces/porting.interface";
import { AddIcCallOutlined, PhoneForwardedOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useModals } from "src/context/ModalContext";
import { FlowMobilePlanConfig } from "../..";
import { COMPANY_NUMBERS_KEY } from "../NumbersConfiguration/query/company-numbers.queries";
import { linkPaidPorting } from "../PaySetupFee/flow-mobile-stripe.actions";
import { ICreateSetupFeeOrderPayload } from "../PaySetupFee/pay.setup.fee.interfaces";
import { BuyPhoneNumber } from "./components/BuyPhoneNumber";
import { MethodCard } from "./components/MethodCard";
import { PlanSummaryCard } from "./components/PlanSummaryCard";
import { SetupFeeBar } from "./components/SetupFeeBar";
import { SetupPaymentResult } from "./components/SetupPaymentResult";
import { useFlowMobileSetupSync } from "./hooks/useFlowMobileSetupSync";
import { useSetupFeesQuery } from "./queries/setup-fees.queries";

export type SetupMethod = "PORTING" | "BUYING";

const PORTING_DATA_DEBOUNCE_MS = 400;

export function SetupNumber({ plan }: { plan: FlowMobilePlanConfig }) {
  const { closeByKey } = useModals();
  const queryClient = useQueryClient();
  const { data: configurations } = useSetupFeesQuery();

  const [method, setMethod] = useState<SetupMethod>("PORTING");

  const SETUP_FEE = useMemo(() => {
    const portingFeeItem = configurations?.find((c) => c.name === "PORTING_SETUP_FEE");
    return portingFeeItem?.value ? parseFloat(portingFeeItem.value) : 25;
  }, [configurations]);

  const [draftPortingData, setDraftPortingData] = useState<PortingFormData | null>(null);

  const [portingData, setPortingData] = useState<PortingFormData | null>(null);

  const [selectedBuyingNumber, setSelectedBuyingNumber] = useState<PhoneNumber | null>(null);

  const [portingInProgress, setPortingInProgress] = useState<{ phoneNumber: string | null } | null>(
    plan.externalId ? { phoneNumber: plan.phoneNumber || null } : null,
  );

  const [paymentState, setPaymentState] = useState<{
    status: "idle" | "success" | "error" | "pending";
    message?: string;
    data?: unknown;
  }>({
    status: "idle",
  });

  const isPorting = method === "PORTING";
  const isPaymentSuccess = paymentState.status === "success";
  const isPaymentError = paymentState.status === "error";

  useEffect(() => {
    if (plan.externalId || !plan.subscriptionId) return;

    let cancelled = false;
    const checkExistingPorting = async () => {
      try {
        const link = await linkPaidPorting(String(plan.subscriptionId));
        if (cancelled || !link.linked) return;
        setPortingInProgress({ phoneNumber: link.phoneNumber });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEYS.self("flow_mobile") }),
          queryClient.invalidateQueries({ queryKey: COMPANY_NUMBERS_KEY }),
        ]);
      } catch (error) {
        console.error("Flow Mobile porting link check failed:", error);
      }
    };

    void checkExistingPorting();
    return () => {
      cancelled = true;
    };
  }, [plan.externalId, plan.subscriptionId, queryClient]);

  useEffect(() => {
    if (isPaymentSuccess) return;

    const timeoutId = window.setTimeout(() => {
      setPortingData(draftPortingData);
    }, PORTING_DATA_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [draftPortingData, isPaymentSuccess]);

  const setupPayload: ICreateSetupFeeOrderPayload | null = useMemo(() => {
    if (isPorting) {
      if (!portingData) return null;

      return {
        method: "PORTING",
        planId: plan.planId,
        subscriptionId: plan.subscriptionId,
        companyId: plan.companyId,
        setupFee: SETUP_FEE,
        porting: portingData,
      };
    }

    if (!selectedBuyingNumber) return null;

    return {
      method: "BUYING",
      planId: plan.planId,
      subscriptionId: plan.subscriptionId,
      companyId: plan.companyId,
      setupFee: 0,
      buying: selectedBuyingNumber,
    };
  }, [isPorting, portingData, selectedBuyingNumber, plan.planId, plan.subscriptionId, plan.companyId, SETUP_FEE]);

  const isSetupDisabled = useMemo(() => {
    if (isPaymentSuccess) return true;

    return isPorting ? !portingData : !selectedBuyingNumber;
  }, [isPaymentSuccess, isPorting, portingData, selectedBuyingNumber]);

  const handleChangeMethod = useCallback((nextMethod: SetupMethod) => {
    setMethod(nextMethod);
    setPaymentState({ status: "idle" });
  }, []);

  const handlePaymentSuccess = useCallback(async (data: unknown) => {
    setTimeout(() => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: COMPANY_NUMBERS_KEY,
          refetchType: "active",
        }),

        queryClient.invalidateQueries({
          queryKey: SUBSCRIPTION_KEYS.self("flow_mobile"),
          refetchType: "active",
        }),
      ]);

      closeByKey(`Dashboard-Setup-${plan.subscriptionId}`);
    }, 3000);

    setPaymentState({
      status: "success",
      message: "Setup fee paid successfully.",
      data,
    });
  }, []);

  const handlePaymentPending = useCallback((message: string) => {
    setPaymentState({
      status: "pending",
      message,
    });
  }, []);

  const handlePaymentError = useCallback((error: unknown) => {
    console.error("Setup fee payment error:", error);

    setPaymentState({
      status: "error",
      message: "Could not complete the payment.",
      data: error,
    });
  }, []);

  const handleSetupErrorMessage = useCallback((message: string) => {
    setPaymentState({ status: "error", message });
  }, []);

  useFlowMobileSetupSync({
    companyId: plan.companyId,
    subscriptionId: plan.subscriptionId,
    onProvisioned: handlePaymentSuccess,
    onPending: handlePaymentPending,
    onError: handleSetupErrorMessage,
  });

  const handleRetryPayment = useCallback(() => {
    setPaymentState({ status: "idle" });
  }, []);

  if (portingInProgress && paymentState.status === "idle") {
    const progressMessage = portingInProgress.phoneNumber
      ? `The request for ${portingInProgress.phoneNumber} was already submitted with the porting payment.`
      : "This line already has a porting request submitted with the payment.";

    return (
      <Box sx={{ px: 3, py: 4 }}>
        <Box
          sx={{
            maxWidth: 560,
            mx: "auto",
            borderRadius: "18px",
            border: "1px solid rgba(251,142,0,0.28)",
            bgcolor: "rgba(251,142,0,0.08)",
            px: 3,
            py: 3,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", color: "#FB8E00" }}>PORTING</Typography>
          <Typography sx={{ mt: 1, fontSize: 24, fontWeight: 900, letterSpacing: "-0.03em" }}>Porting in progress</Typography>
          <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)", lineHeight: 1.5 }}>
            {progressMessage} This line stays in progress until the carrier completes the transfer. A new porting request is not available for this
            plan.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isPaymentSuccess) {
    return (
      <SetupPaymentResult
        status="success"
        title="Payment completed successfully"
        message="Your Flow Mobile setup has started. Enjoy your assistant phone line experience."
      />
    );
  }

  if (paymentState.status === "pending") {
    return (
      <SetupPaymentResult
        status="pending"
        title="Waiting for payment"
        message={paymentState.message || "This step stays blocked until the payment is confirmed."}
      />
    );
  }

  if (isPaymentError) {
    return (
      <SetupPaymentResult
        status="error"
        title="Payment could not be completed"
        message={paymentState.message || "Something went wrong while processing your setup payment. Please try again."}
        actionLabel="Try again"
        onAction={handleRetryPayment}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        mx: "auto",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 900,
              color: "#FB8E00",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              lineHeight: 1.1,
            }}
          >
            Flow Mobile Setup
          </Typography>

          <Typography
            sx={{
              mt: 0.4,
              fontSize: 26,
              fontWeight: 900,
              color: "var(--fx-surface-text, #0F172A)",
              letterSpacing: "-0.03em",
              lineHeight: 1.15,
            }}
          >
            Connect your assistant phone line
          </Typography>

          <Typography
            sx={{
              mt: 0.8,
              fontSize: 14,
              color: "var(--fx-surface-textsecondary, #64748B)",
              fontWeight: 500,
              maxWidth: 720,
              lineHeight: 1.4,
            }}
          >
            Your {plan.name} plan is active. Set up a business number to enable calls and messages.
          </Typography>
        </Box>

        <PlanSummaryCard planName={plan.name} benefits={plan.features} />

        <Box sx={{ mt: 2.4 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 900,
              color: "var(--fx-surface-text, #0F172A)",
              mb: 1.2,
            }}
          >
            Choose setup method
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <MethodCard
              selected={method === "PORTING"}
              title="Port existing number"
              description="Keep your current business number and transfer it to your assistant."
              meta="Semi-automated · May take several days"
              icon={<PhoneForwardedOutlined sx={{ fontSize: 20 }} />}
              onClick={() => handleChangeMethod("PORTING")}
            />

            <MethodCard
              selected={method === "BUYING"}
              title="Activate new number"
              description="Search for an available number and activate it automatically."
              meta="Automated · Fastest option"
              icon={<AddIcCallOutlined sx={{ fontSize: 20 }} />}
              onClick={() => handleChangeMethod("BUYING")}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 3, pb: 3 }}>
        <Box
          sx={{
            borderTop: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
            pt: 2.5,
          }}
        >
          {isPorting ? (
            <PhoneAccessRequest hideActions onFormChange={setDraftPortingData} />
          ) : (
            <BuyPhoneNumber hideActions selected={selectedBuyingNumber} onSelectedChange={setSelectedBuyingNumber} />
          )}
        </Box>

        <SetupFeeBar
          isPorting={isPorting}
          setupFee={SETUP_FEE}
          disabled={isSetupDisabled}
          setupPayload={setupPayload}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
          onPaymentPending={handlePaymentPending}
        />
      </Box>
    </Box>
  );
}
